import React, {useCallback, useEffect, useState} from 'react';
import {useIntl} from 'react-intl';
import cn from 'classnames';
import Cookies from 'js-cookie';

import CountryModel from 'coreClasses/countries/CountryModel';
import {useLocalesMgr} from 'coreHooks';

import {useGeneralProvider} from 'brand/app/components/_app-config/GeneralProvider';
import globalMessage from 'brand/app/components/messages';
import Button from 'brandComponents/Button';
import Dropdown from 'brandComponents/Dropdown';
import Icon from 'brandComponents/Icon';
import {POPUP_COOKIE_NAME} from 'brandComponents/popups/CountryRedirectPopup/ICountryRedirectPopup';

import {ICountryLanguageSelectorProps} from './ICountryLanguageSelector';
import messages from './messages';

import styles from './CountryLanguageSelector.module.scss';

const CountryLanguageSelector = ({
	classNames,
	shouldFetchGeolocation = false,
	popupsShouldBeClosed,
}: ICountryLanguageSelectorProps) => {
	const {managerRepository} = useGeneralProvider();
	const localesMgr = useLocalesMgr();
	const defaultLocale = localesMgr.getDefaultLocale();
	const intl = useIntl();
	const [countryCode, setCountryCode] = useState(defaultLocale.getCountryCode());
	const [language, setLanguage] = useState(defaultLocale.getLanguageCode());
	const getLocaleFromCode = (code: string) => globalMessage[code]?.defaultMessage[0].value;
	const displayValue = intl.formatMessage(globalMessage[countryCode]);

	useEffect(() => {
		const fetchGeoLocation = async () => {
			const geolocation = await localesMgr.fetchGeolocation();

			handleChangeCountry(geolocation.country.getCountryCode());
		};

		if (shouldFetchGeolocation) {
			fetchGeoLocation();
		}
	}, [shouldFetchGeolocation]);

	// takes countries and find intl localized message to show in the list using country code
	const countriesDataBuilder = (countries: CountryModel[]) => {
		const countriesArray = countries.map((country) => {
			const countryMessage = {
				id: `country.${country.getCountryCode()}`,
				defaultMessage: country.getCountryCode(),
			};

			return {
				value: country.getCountryCode(),
				label: intl.formatMessage(countryMessage),
			};
		});

		return countriesArray.sort((a, b) => a.label?.localeCompare(b?.label));
	};

	const localesDataBuilder = useCallback((countries: CountryModel[]) => {
		const countrySelect = countries?.find((countryModel) => countryModel.getCountryCode() === countryCode);
		const availableLanguageArr = countrySelect?.getAvailableLanguages() ?? [];
		const availableLanguage = availableLanguageArr.map(langCode => ({
			label: globalMessage[langCode]?.defaultMessage[0].value,
			value: langCode
		}));

		return availableLanguage;

	}, [countryCode]);

	const countries = managerRepository.getCountriesMgr().getCountries();
	const countriesData = countriesDataBuilder(countries);
	const localesData = localesDataBuilder(countries);

	const handleChangeLanguage = useCallback((newValue: string) => {
		setLanguage(newValue);
	}, []);

	const handleChangeCountry = useCallback((newValue: string) => {
		// prevent country redirect pop up opening if changes requested by user
		if (!Cookies.get(POPUP_COOKIE_NAME)) {
			Cookies.set(POPUP_COOKIE_NAME, 'true', {expires: 365});
		}

		setCountryCode(newValue);

		const countrySelect = countries?.find((countryModel) => countryModel.getCountryCode() === newValue);
		const defaultLanguage = countrySelect.getDefaultLanguage();

		if (defaultLanguage) {
			setLanguage(countrySelect.getDefaultLanguage());
		}
	}, []);

	const handleClick = async () => {
		await localesMgr.updateLocale(countryCode, language);
		// set cookie to prevent popup from showing immediately after redirect
		Cookies.set(POPUP_COOKIE_NAME, 'true', {expires: 1});

		if (popupsShouldBeClosed) {
			managerRepository.getPopupsMgr().closeAllPopups();
		}
	};

	return (
		<div className={cn(styles['country-language-selector__wrapper'], classNames?.wrapper)}>
			<Dropdown value={countryCode}>
				<Dropdown.Trigger
					label={intl.formatMessage(messages.labelCountry)}
					customIcon={<Icon name="arrow-down" />}
					displayValue={displayValue}
				/>
				<Dropdown.Menu
					closable
					data={countriesData}
					onSelect={handleChangeCountry}
					classNames={cn(styles['country-language-selector__menu'], classNames?.menu)}
					behavioralBreakpoints={{
						mobile: '<=small',
						desktop: '>small'
					}}
				/>
			</Dropdown>
			<Dropdown value={language}>
				<Dropdown.Trigger
					label={intl.formatMessage(messages.labelLanguage)}
					customIcon={<Icon name="arrow-down" />}
					displayValue={getLocaleFromCode(language)}
				/>
				<Dropdown.Menu
					closable
					data={localesData}
					onSelect={handleChangeLanguage}
					classNames={cn(styles['country-language-selector__menu'], classNames?.menu)}
					behavioralBreakpoints={{
						mobile: '<=small',
						desktop: '>small'
					}}
				/>
			</Dropdown>
			<Button
				variant="primary"
				onClick={(e) => {
					e.preventDefault();
					handleClick();
				}}
				classNames={cn(styles['country-language-selector__button'], classNames?.button)}
			>
				{intl.formatMessage(messages.confirmButton)}
			</Button>
		</div>
	);
};

export default CountryLanguageSelector;
